export const dashboardRoles = [
  "developer",
  "admin",
  "analyst",
  "publisher",
  "editor",
  "viewer",
  "producer",
  "bot",
  "taiyoro-viewer",
  "taiyoro-publisher",
  "taiyoro-producer",
  "taiyoro-editor",
  "taiyoro-bot",
  "taiyoro-analyst",
  "taiyoro-super-user",
  "playbrain-viewer",
  "playbrain-publisher",
  "playbrain-producer",
  "playbrain-editor",
  "playbrain-bot",
  "playbrain-analyst",
  "playbrain-super-user",
  "playbrain-users-super-user",
  "playbrain-roles-super-user",
  "administrator",
] as const

export type DashboardRole = (typeof dashboardRoles)[number]

export type AuthUser = {
  sub?: string
  dashboardLanguage?: "en" | "ja"
  email?: string
  name?: string
  permissions?: unknown
  profileImageUrl?: string
  /** The role key from SQL, hydrated onto the session. Never read from Auth0. */
  role?: unknown
  /** The role keys from SQL, hydrated onto the session. */
  roles?: unknown
  username?: string
  /**
   * Kept only because the users API still mirrors it for consumers that have
   * not migrated. It is never an authorization source here — the roles and
   * permissions come from the database, through the Roles page.
   */
  user_metadata?: {
    role?: unknown
    roles?: unknown
    username?: unknown
  }
  [claim: string]: unknown
}

const dashboardRoleKeyPattern = /^[a-z][a-z0-9-]{1,62}$/

export function isDashboardRole(value: unknown): value is DashboardRole {
  return dashboardRoles.includes(value as DashboardRole)
}

/**
 * The session's roles, as hydrated from SQL by `withDashboardSqlProfile`.
 *
 * Auth0 `user_metadata` is deliberately not consulted: it is a mirror the Roles
 * page cannot reach, so reading it meant a role edited in the dashboard had no
 * effect until the token was reissued.
 */
function rawUserRoles(user: AuthUser | null | undefined): string[] {
  if (!user) return []
  const values = [user.role, user.roles].flatMap((value) => {
    if (Array.isArray(value)) return value
    return value ? [value] : []
  })
  return Array.from(new Set(values.filter((value): value is string => typeof value === "string")))
}

export function isDashboardRoleKey(value: unknown): value is string {
  return (
    typeof value === "string" &&
    dashboardRoleKeyPattern.test(value) &&
    value !== "none" &&
    value !== "superadmin"
  )
}

export function getUserRoleKeys(user: AuthUser | null | undefined): string[] {
  return rawUserRoles(user).filter(isDashboardRoleKey)
}

export function getUserRoles(user: AuthUser | null | undefined): DashboardRole[] {
  const values = rawUserRoles(user)
  return Array.from(new Set(values.filter(isDashboardRole)))
}

export function hasDashboardRole(user: AuthUser | null | undefined, roles: DashboardRole[]): boolean {
  if (roles.length === 0) return Boolean(user)
  const userRoles = getUserRoles(user)
  return roles.some((role) => userRoles.includes(role))
}

function getDashboardPermissions(user: AuthUser | null | undefined): string[] {
  if (!user || !Array.isArray(user.permissions)) return []
  return user.permissions.filter((permission): permission is string => typeof permission === "string")
}

function hasDashboardPermission(user: AuthUser | null | undefined, permission: string): boolean {
  return getDashboardPermissions(user).includes(permission)
}

export function canUseDashboardPermission(
  user: AuthUser | null | undefined,
  resource: string,
  action: string
): boolean {
  return hasDashboardPermission(user, `${resource}:${action}`)
}

export function canEditContent(user: AuthUser | null | undefined): boolean {
  return getDashboardPermissions(user).some((permission) =>
    /^playbrain\.website\.[^:]+:(create|write|publish)$/.test(permission)
  )
}

export function canReadDashboardUsers(user: AuthUser | null | undefined): boolean {
  return hasDashboardPermission(user, "playbrain.users:read")
}

export function canReadDashboardRoles(user: AuthUser | null | undefined): boolean {
  return hasDashboardPermission(user, "playbrain.roles:read")
}

export function canAdminDashboard(user: AuthUser | null | undefined): boolean {
  return canReadDashboardUsers(user) || canReadDashboardRoles(user)
}
